/*
 * Vehicle Picker Script
 *
 * Powers the Year / Make / Model search on the Duallys Only home page.
 * It reads window.duallyVehicleData (defined in vehicle-data.js),
 * builds the dropdown options, then looks up the matching bolt pattern
 * and redirects the customer to a search page filtered by that pattern.
 */

document.addEventListener('DOMContentLoaded', function() {
  const data = window.duallyVehicleData || [];
  if (!data.length) return;

  const yearSelect = document.getElementById('vehicle-year');
  const makeSelect = document.getElementById('vehicle-make');
  const modelSelect = document.getElementById('vehicle-model');
  const searchBtn = document.getElementById('vehicle-search-btn');

  // Elements used to display the selected vehicle and reset action on the homepage
  const selectedInfo = document.getElementById('selected-vehicle-info');
  const selectedText = document.getElementById('selected-vehicle-text');
  const resetBtnEl = document.getElementById('vehicle-reset-btn');
  const resetInlineBtn = document.getElementById('vehicle-reset-inline');

  if (!yearSelect || !makeSelect || !modelSelect || !searchBtn) return;

  /**
   * Save selected vehicle to localStorage and update the UI.
   * @param {number} year
   * @param {string} make
   * @param {string} model
   */
  /**
   * Persist the selected vehicle details to localStorage.
   *
   * In addition to year/make/model, this helper optionally accepts a
   * bolt pattern. When provided, the bolt pattern is stored so that
   * subsequent pages (e.g. wheels or packages collections) can filter
   * products accordingly. Without a bolt pattern, only the year/make/model
   * values are saved.
   *
   * @param {number} year
   * @param {string} make
   * @param {string} model
   * @param {string|null} bolt
   */
  function saveSelectedVehicle(year, make, model, bolt = null) {
    try {
      const payload = { year: year, make: make, model: model };
      if (bolt) {
        payload.bolt = bolt;
        if (window.duallyVehicleSeo && typeof window.duallyVehicleSeo.buildSlug === 'function') {
          payload.seoSlug = window.duallyVehicleSeo.buildSlug(year, make, model, bolt);
          payload.seoPath = window.duallyVehicleSeo.buildPrettyPath(year, make, model, bolt);
        }
      }
      localStorage.setItem('selectedVehicle', JSON.stringify(payload));
      updateSelectedDisplay(payload);
    } catch (e) {
      console.error('Failed to save selected vehicle', e);
    }
  }

  /**
   * Clear the stored selection and hide the selected info UI.
   */
  function clearSelectedVehicle() {
    try {
      localStorage.removeItem('selectedVehicle');
    } catch (e) {
      console.error('Failed to clear selected vehicle', e);
    }
    if (selectedInfo) {
      selectedInfo.style.display = 'none';
    }
    if (resetInlineBtn) {
      resetInlineBtn.hidden = true;
      resetInlineBtn.style.display = 'none';
    }
    // Reset the selects to their default state
    if (yearSelect) {
      yearSelect.value = '';
      yearSelect.dispatchEvent(new Event('change'));
    }
    if (makeSelect) {
      makeSelect.innerHTML = '<option value="">Make</option>';
    }
    if (modelSelect) {
      modelSelect.innerHTML = '<option value="">Model</option>';
    }

    // Clear collection/search filtering caused by the stored vehicle selection.
    const path = window.location.pathname;
    if (/\/collections\//.test(path) || /\/search/.test(path)) {
      window.location.href = path;
    }
  }

  /**
   * Update the selected vehicle text and show/hide the info container.
   * @param {{year: number, make: string, model: string}} selected
   */
  function updateSelectedDisplay(selected) {
    if (!selected || !selected.year || !selected.make || !selected.model) {
      if (selectedInfo) selectedInfo.style.display = 'none';
      if (resetInlineBtn) {
        resetInlineBtn.hidden = true;
        resetInlineBtn.style.display = 'none';
      }
      return;
    }

    if (selectedInfo && selectedText) {
      selectedText.textContent = `Your Vehicle: ${selected.year} ${selected.make} ${selected.model}`;
      selectedInfo.style.display = 'flex';
    }

    if (resetInlineBtn) {
      resetInlineBtn.hidden = false;
      resetInlineBtn.style.display = 'inline-flex';
    }
  }

  // Build list of all distinct years covered by the dataset
  const years = [];
  data.forEach(item => {
    const start = parseInt(item.start, 10);
    const end = parseInt(item.end, 10);
    if (isNaN(start) || isNaN(end)) return;
    for (let y = start; y <= end; y++) {
      if (!years.includes(y)) {
        years.push(y);
      }
    }
  });
  // Show newest year first in the dropdown (e.g., 2025 → 1988)
  years.sort((a, b) => b - a);

  years.forEach(year => {
    const opt = document.createElement('option');
    opt.value = String(year);
    opt.textContent = String(year);
    yearSelect.appendChild(opt);
  });

  // When year changes, clear and populate makes for that year only
  yearSelect.addEventListener('change', function() {
    const yearValue = parseInt(this.value, 10);
    makeSelect.innerHTML = '<option value="">Make</option>';
    modelSelect.innerHTML = '<option value="">Model</option>';

    if (!yearValue) return;

    const makes = Array.from(
      new Set(
        data
          .filter(item => {
            const start = parseInt(item.start, 10);
            const end = parseInt(item.end, 10);
            return !isNaN(start) && !isNaN(end) && yearValue >= start && yearValue <= end;
          })
          .map(item => item.make)
      )
    ).sort();

    makes.forEach(make => {
      const opt = document.createElement('option');
      opt.value = make;
      opt.textContent = make;
      makeSelect.appendChild(opt);
    });
  });

  // When make changes, update models for that specific year + make
  makeSelect.addEventListener('change', function() {
    const yearValue = parseInt(yearSelect.value, 10);
    const selectedMake = this.value;
    modelSelect.innerHTML = '<option value="">Model</option>';

    if (!yearValue || !selectedMake) return;

    const models = Array.from(
      new Set(
        data
          .filter(item => {
            const start = parseInt(item.start, 10);
            const end = parseInt(item.end, 10);
            return (
              item.make === selectedMake &&
              !isNaN(start) &&
              !isNaN(end) &&
              yearValue >= start &&
              yearValue <= end
            );
          })
          .map(item => item.model)
      )
    ).sort();

    models.forEach(model => {
      const opt = document.createElement('option');
      opt.value = model;
      opt.textContent = model;
      modelSelect.appendChild(opt);
    });
  });

  // Handle search click – look up bolt pattern and redirect
  searchBtn.addEventListener('click', function() {
    const yearValue = parseInt(yearSelect.value, 10);
    const makeValue = makeSelect.value;
    const modelValue = modelSelect.value;

    if (!yearValue || !makeValue || !modelValue) {
      alert('Please select a year, make and model.');
      return;
    }

    const match = data.find(item => {
      const start = parseInt(item.start, 10);
      const end = parseInt(item.end, 10);
      return (
        item.make === makeValue &&
        item.model === modelValue &&
        !isNaN(start) &&
        !isNaN(end) &&
        yearValue >= start &&
        yearValue <= end
      );
    });

    if (match && match.bolt) {
      const bolt = match.bolt;
      // Save the current selection along with the bolt pattern before redirecting so it
      // can be restored and used to filter product collections on subsequent pages.
      saveSelectedVehicle(yearValue, makeValue, modelValue, bolt);
      let searchUrl = '/search?q=' + encodeURIComponent(bolt) + '&options%5Bprefix%5D=none';
      if (window.duallyVehicleSeo && typeof window.duallyVehicleSeo.buildSlug === 'function') {
        const seoSlug = window.duallyVehicleSeo.buildSlug(yearValue, makeValue, modelValue, bolt);
        if (seoSlug) {
          searchUrl += '&ymm=' + encodeURIComponent(seoSlug);
        }
      }
      window.location.href = searchUrl;
    } else {
      alert('No bolt pattern found for the selected vehicle.');
    }
  });

  // Restore previously selected vehicle (if any) from localStorage when the page loads
  (function restoreSelectedVehicle() {
    let stored;
    try {
      stored = JSON.parse(localStorage.getItem('selectedVehicle'));
    } catch (e) {
      stored = null;
    }
    if (!stored || !stored.year) return;
    // Preselect the year and trigger change to populate make options
    yearSelect.value = String(stored.year);
    yearSelect.dispatchEvent(new Event('change'));
    // Wait for the makes to populate then select the saved make
    setTimeout(() => {
      makeSelect.value = stored.make || '';
      makeSelect.dispatchEvent(new Event('change'));
      // Wait again for models then set the saved model
      setTimeout(() => {
        modelSelect.value = stored.model || '';
        updateSelectedDisplay(stored);
      }, 100);
    }, 100);
  })();

  // Attach click handlers to reset controls to clear selection
  if (resetBtnEl) {
    resetBtnEl.addEventListener('click', function() {
      clearSelectedVehicle();
    });
  }

  if (resetInlineBtn) {
    resetInlineBtn.addEventListener('click', function() {
      clearSelectedVehicle();
    });
  }
});
